import React, { forwardRef, useImperativeHandle, type ForwardedRef } from 'react';

import { usePaginatedTableV8 } from './hooks/use-paginated-table-v8';
import { useTableEditing } from './hooks/use-table-editing';
import { TableV8Chrome } from './table-v8-chrome';
import type {
  TableEditableV8Component,
  TableEditableV8Props,
  TablePaginatedV8Ref,
} from './table-types';

export type { TableEditableV8Props, TableEditableV8Component };

function TableEditableV8Inner<TData extends object>(
  props: TableEditableV8Props<TData>,
  ref: ForwardedRef<TablePaginatedV8Ref<TData>>,
) {
  const {
    id,
    data,
    tableStore = null,
    updateData = null,
    onBeforeEditRow = null,
    onBeforeNewRow = null,
    onBeforeSaveRow = null,
    onBeforeDeleteRow = null,
    onSaveRow = null,
    onNewRow = null,
    deleteRow = null,
    ...coreProps
  } = props;

  const editing = useTableEditing<TData>({
    tableId: id,
    defaultData: data,
    tableStore,
    updateData,
    onBeforeEditRow,
    onBeforeNewRow,
    onBeforeSaveRow,
    onBeforeDeleteRow,
    onSaveRow,
    onNewRow,
    deleteRow,
  });

  const tableState = usePaginatedTableV8({ ...coreProps, id, data }, editing);

  useImperativeHandle(ref, () => ({ table: tableState.table }), [tableState.table]);

  return (
    <TableV8Chrome
      props={props}
      table={tableState.table}
      displayData={tableState.displayData}
      saveColumns={tableState.saveColumns}
      scrollContainerStyle={tableState.scrollContainerStyle}
      handlePageChange={tableState.handlePageChange}
      handlePageSizeChange={tableState.handlePageSizeChange}
      tableStore={tableStore}
    />
  );
}

const TableEditableV8Base = forwardRef(TableEditableV8Inner);
TableEditableV8Base.displayName = 'TableEditableV8';

/** Server-side paginated grid with optional inline row editing. */
export const TableEditableV8 = TableEditableV8Base as TableEditableV8Component;

/** @deprecated Use {@link TableEditableV8}. Kept for backward compatibility. */
export const TablePaginatedV8 = TableEditableV8;
